/**
 * One rule for "are the CRFs ready", used by every reader.
 *
 * THE THIRD INSTANCE OF ONE SHAPE. `8228ce98` fixed "needs variables" answered
 * differently by four readers. The 2026-08-28 walkthrough found two more inside
 * two hours: the Study Documents hub told a customer "Case report forms have not
 * been generated or uploaded" while 38 forms carrying 781 variables existed, and
 * the CRF Builder announced "38 forms ready to approve — no blocking issues"
 * directly above an approve action that refused on a form-code collision.
 *
 * WHY THE HUB WAS WRONG, precisely — the message was not careless. `crfStats`
 * counts `artifacts` where `type == 'crf'`, and those are written BY APPROVAL;
 * the builder's working forms live in `crfBuilder/session.forms`. The hub was
 * truthfully reporting what it could see, having been asked a question its store
 * cannot answer. Measured on the walkthrough study: 0 crf artifacts before
 * approval, 38 after, with 38 session forms throughout.
 *
 * SO THE STATE IS THREE-VALUED, and collapsing it to a boolean IS the defect.
 * "No forms at all" and "forms exist, none approved" are different situations
 * needing different words and a different next action from the customer.
 */

export type CrfReadinessState = 'unknown' | 'none' | 'unapproved' | 'ready';

export interface CrfReadinessInput {
  /** Forms in the CRF Builder's working session. `null` while loading. */
  sessionFormCount: number | null;
  /** Forms that reached approval (crf artifacts). `null` while loading. */
  approvedArtifactCount: number | null;
  /** Conditions that refuse approval outright, e.g. a form-code collision. */
  blockingIssues?: string[];
}

export interface CrfReadiness {
  state: CrfReadinessState;
  /** Safe to run downstream generation (data dictionary, edit checks, guide). */
  ready: boolean;
  /** Something refuses approval regardless of counts. */
  blocked: boolean;
  awaitingApproval: number;
  blockingIssues: string[];
}

export function crfReadiness(input: CrfReadinessInput): CrfReadiness {
  const blockingIssues = input.blockingIssues ?? [];
  const blocked = blockingIssues.length > 0;

  // Loading is NOT absence. Reporting "no forms" while still reading is how a
  // customer gets told their work is missing.
  if (input.sessionFormCount === null || input.approvedArtifactCount === null) {
    return { state: 'unknown', ready: false, blocked, awaitingApproval: 0, blockingIssues };
  }

  const total = input.sessionFormCount;
  const approved = input.approvedArtifactCount;
  const awaitingApproval = Math.max(0, total - approved);

  if (total === 0 && approved === 0) {
    return { state: 'none', ready: false, blocked, awaitingApproval: 0, blockingIssues };
  }
  if (approved === 0 || awaitingApproval > 0) {
    return { state: 'unapproved', ready: false, blocked, awaitingApproval, blockingIssues };
  }
  return { state: 'ready', ready: !blocked, blocked, awaitingApproval: 0, blockingIssues };
}

/**
 * What to tell the customer. Empty when there is nothing in their way.
 *
 * The old text — "Case report forms have not been generated or uploaded for this
 * study" — survives ONLY in the state where it is true. It was not wrong
 * everywhere; it was wrong in the one state that actually occurred.
 */
export function crfReadinessMessage(r: CrfReadiness): string {
  if (r.blocked) {
    return `Approval is blocked: ${r.blockingIssues.join('; ')}.`;
  }
  switch (r.state) {
    case 'unknown':
      return 'Checking case report forms…';
    case 'none':
      return 'Case report forms have not been generated or uploaded for this study. ' +
        'Complete the CRF Builder first.';
    case 'unapproved':
      return `${r.awaitingApproval} case report form${r.awaitingApproval === 1 ? '' : 's'} ` +
        'await approval. Approve them in the CRF Builder to continue.';
    case 'ready':
    default:
      return '';
  }
}
